import { LISTING_COMMENTS } from "@/graphql/queries/listing-comments";
import { useCallback, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import css from "./.module.scss";
import Image from "next/image";
import Icon from "@/components/icon";
import { useAuthState } from "@/contexts/auth/auth.provider";
import {
  ADD_ANSWER,
  ADD_COMMENT,
  DELETE_COMMENT,
  HIDE_COMMENT,
  UPDATE_COMMENTS_ENABLED,
} from "@/graphql/mutations/listing-comments";
import { Checkbox } from "@aqar-app/react-ui";
import { ErrorModal } from "@/components/error/ssr";
import Link from "next/link";
import { usePath } from "@/contexts/path/path";
import { useRouter } from "next/router";
import logger from "@/console";
import dayjs from "dayjs";
import "dayjs/locale/ar"; // use loaded locale globally
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.locale("ar");
dayjs.extend(relativeTime);
function mapComment(comment) {
  return {
    id: comment.comment_id,
    createdAt: comment.created_at,
    deletedAt: comment.deleted_at,
    text: comment.comment,
    listingId: comment.listing_id,
    author: {
      name: comment.commenter_name,
      img: comment.commenter_img,
      id: comment.created_by,
    },
    reply: {
      text: comment.answer,
      author: {
        name: comment.answered_by_name,
        img: comment.answered_by_img,
        id: comment.answered_by,
      },
      createdAt: comment.answered_at,
    },
  };
}

export function ListingComments({ listing }) {
  const { user, token } = useAuthState();
  const { updateContext } = usePath();
  const router = useRouter();

  const isOwner =
    user &&
    (listing.user_id === user?.user_id ||
      listing.employee_user_id === user?.user_id);
  const [updateCommentsEnabled] = useMutation(UPDATE_COMMENTS_ENABLED, {
    context: { token },
  });
  const [commentsEnabled, setCommentsEnabled] = useState(
    listing.comments_enabled,
  );
  const [comments, setComments] = useState({
    list: [],
    total: 0,
  });
  const { error, fetchMore } = useQuery(LISTING_COMMENTS, {
    skip: !listing?.id,
    variables: {
      listing_id: listing.id,
      last_comment_id: null,
      page_size: 100,
    },
    onCompleted: (data) => {
      setComments({
        list: (data?.ListingComments?.getListingComments?.comments || []).map(
          (comment) => mapComment(comment),
        ),
        total: data?.ListingComments?.getListingComments?.total || 0,
      });
    },
  });

  if (!listing?.comments_enabled && user?.user_id !== listing?.user_id)
    return null;
  return (
    <div className={css.list}>
      <div className={css.header}>
        <h4>أسئلة المستخدمين ({comments.total})</h4>
        {isOwner ? (
          <Checkbox
            switch
            onChange={(e) => {
              updateCommentsEnabled({
                variables: {
                  listing_id: listing.id,
                  enabled: !commentsEnabled,
                },
              })
                .then(({ errors }) => {
                  setCommentsEnabled(!commentsEnabled);
                })
                .catch(ErrorModal.handle);
            }}
            checked={commentsEnabled}
          >
            تفعيل التعليقات
          </Checkbox>
        ) : null}
      </div>
      {(comments.list || []).length === 0 ? (
        <>
          {isOwner ? (
            <div className={css.noComments}>
              <Icon name="Message" width={40} height={40} />
              <Checkbox
                switch
                onChange={(e) => {
                  updateCommentsEnabled({
                    variables: {
                      listing_id: listing.id,
                      enabled: e.target.checked,
                    },
                  })
                    .then(({ errors }) => {
                      setCommentsEnabled(!commentsEnabled);
                      logger.log(errors);
                    })
                    .catch(ErrorModal.handle);
                }}
                checked={commentsEnabled}
              >
                تفعيل التعليقات
              </Checkbox>
              <p>لا توجد تعليقات بعد </p>
            </div>
          ) : (
            <div className={css.noComments}>
              <Icon name="Message" width={40} height={40} />
              <p>لاتوجد تعليقات بعد، كن أول المعلقين</p>
              {!user ? (
                <button
                  type="button"
                  onClick={() => {
                    router.push(`/login?returnTo=${router.asPath}`);
                    updateContext({
                      listing: { ...listing, __router__: { disabled: true } },
                    });
                  }}
                >
                  قم بتسجيل الدخول من هنا
                </button>
              ) : null}
            </div>
          )}
        </>
      ) : (
        <div className={css.commentList}>
          {comments.list.map((comment) => (
            <Comment
              canDelete={
                !comment.deletedAt &&
                (user?.user_id === comment?.author?.id ||
                  user?.user_id === listing.user_id)
              }
              canHide={!comment.deletedAt && user?.user_id === listing.user_id}
              canReply={!comment.deletedAt && user?.user_id === listing.user_id}
              key={comment.id}
              onCommentDeleted={(id) =>
                setComments({
                  total: comments.total - 1,
                  list: comments.list.filter((c) => c.id !== id),
                })
              }
              onCommentUpdated={(cmnt) => {
                const comment = mapComment(cmnt);
                const newComments = comments.list.slice();
                const idx = comments.list.findIndex((c) => c.id === comment.id);
                newComments[idx] = comment;
                setComments({
                  total: comments.total,
                  list: newComments,
                });
              }}
              token={token}
              {...comment}
            />
          ))}
        </div>
      )}

      {user && !user.iam_verified && user.user_id !== listing.user_id ? (
        <div className={css.iam}>
          <Icon name="Iam" width={40} height={40} />
          <p>نرجو منك توثيق حسابك بالنفاذ الوطني حتى تتمكن من اضافة تعليق</p>
          <Link href={`/user-actions/verify-iam`}>اضغط هنا لتوثيق حسابك</Link>
        </div>
      ) : null}
      {user && user.user_id !== listing.user_id && user.iam_verified ? (
        <NewComment
          onNewComment={(comment) => {
            setComments({
              total: comments.total + 1,
              list: [...comments.list, mapComment(comment)],
            });
          }}
          listingId={listing.id}
          token={token}
        />
      ) : null}
    </div>
  );
}

function NewComment({ listingId, token, onNewComment }) {
  const [comment, setComment] = useState("");
  const [addComment] = useMutation(ADD_COMMENT, {
    context: { token },
  });

  const handleAdd = useCallback(
    function () {
      return addComment({
        variables: {
          listing_id: listingId,
          comment,
        },
      }).then(({ data }) => {
        onNewComment(data.ListingComments.addComment);
        setComment("");
      });
    },
    [onNewComment, comment, listingId],
  );
  return (
    <div className={css.add}>
      <textarea
        placeholder="اكتب سؤال ...."
        onChange={(e) => setComment(e.target.value)}
        value={comment}
        onKeyDown={(e) => {
          if (!e.shiftKey && e.key === "Enter" && comment) {
            e.preventDefault();
            handleAdd().catch(ErrorModal.handle);
          }
        }}
      />
      <button
        disabled={!comment}
        onClick={(e) => {
          e.target.disabled = true;
          handleAdd()
            .catch(ErrorModal.handle)
            .finally(() => (e.target.disabled = false));
        }}
      >
        <Icon name="send" width={20} height={20} />
      </button>
    </div>
  );
}
function NewAnswer({ commentId, token, onCommentUpdated }) {
  const [answer, setAnswer] = useState("");
  const [addAnswer] = useMutation(ADD_ANSWER, {
    context: { token },
  });

  return (
    <div className={css.add}>
      <textarea
        onKeyDown={(e) => {
          if (!e.shiftKey && e.key === "Enter" && answer) {
            e.preventDefault();
            addAnswer({
              variables: {
                comment_id: commentId,
                answer,
              },
            })
              .then(({ data }) => {
                onCommentUpdated(data.ListingComments.addComment);
                setAnswer("");
              })
              .catch(ErrorModal.handle)
              .finally(() => (e.target.disabled = false));
          }
        }}
        placeholder="نص الرد ...."
        onChange={(e) => setAnswer(e.target.value)}
        value={answer}
      />
      <button
        onClick={(e) => {
          e.target.disabled = true;
          addAnswer({
            variables: {
              comment_id: commentId,
              answer,
            },
          })
            .then(({ data }) => {
              onCommentUpdated(data.ListingComments.addComment);
              setAnswer("");
            })
            .catch(ErrorModal.handle)
            .finally(() => (e.target.disabled = false));
        }}
      >
        <Icon name="send" width={20} height={20} />
      </button>
    </div>
  );
}

function Comment({
  canDelete,
  canHide,
  canReply,
  onCommentDeleted,
  onCommentUpdated,
  author,
  reply,
  text,
  deletedAt,
  createdAt,
  id,
  token,
}) {
  const [deleteComment] = useMutation(DELETE_COMMENT);
  const [hideComment] = useMutation(HIDE_COMMENT);
  const [openReply, setOpenReply] = useState(false);

  return (
    <div className={css.comment}>
      <div className={css.commentContent}>
        <div className={css.avatar}>
          <Image
            alt={author.name}
            unoptimized
            width={35}
            height={35}
            src={`https://cdn.aqar.fm/users/${author?.img || null}`}
          />
        </div>
        <div className={css.content}>
          <p className={css.author}>{author.name}</p>
          <p className={css.date}>{dayjs(deletedAt || createdAt).fromNow()}</p>
          <div className={css.text}>
            <p>{text}</p>
          </div>
        </div>
        <div className={css.extra}>
          {canReply ? (
            <button onClick={() => setOpenReply(true)}>
              <Icon width={15} height={15} name="comments-reply" />
            </button>
          ) : null}
          {canHide ? (
            <button
              onClick={(e) => {
                e.target.disable = true;
                hideComment({
                  context: { token },
                  variables: { comment_id: id },
                })
                  .then(({ data }) => onCommentDeleted(id))
                  .catch(ErrorModal.handle)
                  .finally(() => (e.target.disabled = false));
              }}
            >
              <Icon width={15} height={15} name="invisible-eye" />
            </button>
          ) : null}
          {canDelete ? (
            <button
              onClick={(e) => {
                e.target.disable = true;
                deleteComment({
                  context: { token },
                  variables: { comment_id: id },
                })
                  .then(({ data }) =>
                    onCommentUpdated(data.ListingComments.deleteComment),
                  )
                  .catch(ErrorModal.handle)
                  .finally(() => (e.target.disabled = false));
              }}
            >
              <Icon width={15} height={15} name="trash-colored" />
            </button>
          ) : null}
        </div>
      </div>
      {reply?.text ? <Answer {...reply} /> : null}
      {openReply ? (
        <NewAnswer
          commentId={id}
          token={token}
          onCommentUpdated={onCommentUpdated}
        />
      ) : null}
    </div>
  );
}

function Answer({ author, text, deletedAt, createdAt }) {
  return (
    <div className={css.reply}>
      <div className={css.commentContent}>
        <div className={css.avatar}>
          <Image
            alt={author?.name}
            unoptimized
            width={35}
            height={35}
            src={`https://cdn.aqar.fm/users/${author?.img || null}`}
          />
        </div>
        <div className={css.content}>
          <p className={css.author}>{author.name}</p>
          <p className={css.date}>{dayjs(deletedAt || createdAt).fromNow()}</p>
          <div className={css.text}>
            <p>{text}</p>
          </div>
        </div>
        {/*
         <div className={css.extra}>
          {canReply ? (
            <button onClick={() => setOpenReply(true)}>
              <Icon width={15} height={15} name="comments-reply" />
            </button>
          ) : null}
          {canHide ? (
            <button
              onClick={() =>
                hideComment({
                  context: { token },
                  variables: { comment_id: id },
                }).then(() => onCommentDeleted(id)).catch(ErrorModal.handle)
              }
            >
              <Icon width={15} height={15} name="invisible-eye" />
            </button>
          ) : null}
          {canDelete ? (
            <button
              onClick={() =>
                deleteComment({
                  context: { token },
                  variables: { comment_id: id },
                }).then(({ data }) =>
                  onCommentUpdated(data.ListingComments.deleteComment),
                ).catch(ErrorModal.handle)
              }
            >
              <Icon width={15} height={15} name="trash-colored" />
            </button>
          ) : null}
        </div>
        */}
      </div>
    </div>
  );
}
